import type { AgentAttachment, SessionUpload } from "../../shared/messages.js";

export function renderPromptAttachmentAsText(attachment: AgentAttachment): string {
  switch (attachment.type) {
    case "github_pr": {
      const lines = [`GitHub PR #${attachment.number}: ${attachment.title}`, attachment.url];
      if (attachment.baseRefName) {
        lines.push(`Base: ${attachment.baseRefName}`);
      }
      if (attachment.headRefName) {
        lines.push(`Head: ${attachment.headRefName}`);
      }
      if (attachment.body) {
        lines.push("", attachment.body);
      }
      return lines.join("\n");
    }
    case "github_issue": {
      const lines = [`GitHub Issue #${attachment.number}: ${attachment.title}`, attachment.url];
      if (attachment.body) {
        lines.push("", attachment.body);
      }
      return lines.join("\n");
    }
    case "gitlab_mr": {
      const lines = [`GitLab MR !${attachment.iid}: ${attachment.title}`, attachment.url];
      if (attachment.targetBranch) {
        lines.push(`Target: ${attachment.targetBranch}`);
      }
      if (attachment.sourceBranch) {
        lines.push(`Source: ${attachment.sourceBranch}`);
      }
      if (attachment.body) {
        lines.push("", attachment.body);
      }
      return lines.join("\n");
    }
    case "gitlab_issue": {
      const lines = [`GitLab Issue #${attachment.iid}: ${attachment.title}`, attachment.url];
      if (attachment.body) {
        lines.push("", attachment.body);
      }
      return lines.join("\n");
    }
  }
}

/**
 * Render a human-readable footer that lists every file the user attached to a
 * chat message. The agent (Claude/Codex/OpenCode) doesn't receive the file
 * bytes inline — instead it reads them off disk via its normal file-read tool.
 * The footer tells it where to find them.
 */
export function renderFileAttachmentFooter(uploads: readonly SessionUpload[]): string {
  if (uploads.length === 0) {
    return "";
  }
  const lines: string[] = [
    "",
    "The user attached the following file(s) to this message. Read them with your file-read tool when relevant; the paths are relative to the workspace root:",
  ];
  for (const upload of uploads) {
    const size = formatBytes(upload.size);
    lines.push(`- ${upload.relativePath} (${upload.mimeType}, ${size})`);
  }
  return lines.join("\n");
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function findGitHubPrAttachment(
  attachments: readonly AgentAttachment[] | undefined,
): Extract<AgentAttachment, { type: "github_pr" }> | null {
  if (!attachments) {
    return null;
  }
  return (
    attachments.find(
      (attachment): attachment is Extract<AgentAttachment, { type: "github_pr" }> =>
        attachment.type === "github_pr",
    ) ?? null
  );
}
